import React from "react";

import IconButton from '@material-ui/core/IconButton';
import PhotoCameraRoundedIcon from '@material-ui/icons/PhotoCameraRounded';
import CircularProgress from '@material-ui/core/CircularProgress';

import styles from "./ModelViewer.module.css";

interface ModelViewerAttributes {
  src: string;
  animationName?: string;
  noControls?:boolean;
  noMove?:boolean;
  usdz?: string;
  autoplay?:boolean;
  ar?:boolean;
  opacity?:number;
  modelRef?:any;
  hideShadow?:boolean;
  onLoad?: () => void;
}

function ModelViewer(props: ModelViewerAttributes, ref: any) {

  const {src, noControls,noMove, animationName, autoplay, ar, usdz, onLoad, opacity, hideShadow} = props;

  const modelViewer: any = React.useRef<HTMLElement>();

  const tapDistance = 2;
  let panning = false;
  let panX: number[], panY:number[];
  let startX:number, startY:number;
  let lastX:number, lastY:number;
  let metersPerPixel:number;

  const [progress, setProgress] = React.useState(0);
  // const [opacity, setOpacity] = React.useState(0);

  const [animationTimeout, setAnimationTimeout] = React.useState<any>(null);


  // setTimeout(() => {
  //   if(modelViewer && modelViewer.current){
  //     console.log(modelViewer.current.availableAnimations);
  //     console.log(modelViewer.current.availableVariants);
  //     console.log(modelViewer.current.modelIsVisible);
  //     console.log(modelViewer.current.model);
  //     // console.log(modelViewer.current.getSceneGraph());
  //   }
  // },2000);


  const startPan = () => {
    if(!modelViewer || !modelViewer.current) return;
    const orbit = modelViewer.current.getCameraOrbit();
    const {theta, phi, radius} = orbit;
    const psi = theta - modelViewer.current.turntableRotation;
    metersPerPixel = 0.75 * radius / modelViewer.current.getBoundingClientRect().height;
    panX = [-Math.cos(psi), 0, Math.sin(psi)];
    panY = [
      -Math.cos(phi) * Math.sin(psi),
      Math.sin(phi),
      -Math.cos(phi) * Math.cos(psi)
    ];
    modelViewer.current.interactionPrompt = 'none';
  };
  const movePan = (thisX: number, thisY:number) => {
    if(!modelViewer || !modelViewer.current) return;
    const dx = (thisX - lastX) * metersPerPixel;
    const dy = (thisY - lastY) * metersPerPixel;
    lastX = thisX;
    lastY = thisY;

    const target = modelViewer.current.getCameraTarget();
    target.x += dx * panX[0] + dy * panY[0];
    target.y += dx * panX[1] + dy * panY[1];
    target.z += dx * panX[2] + dy * panY[2];
    modelViewer.current.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;

  };
  const recenter = (pointer:any) => {
    if(!modelViewer || !modelViewer.current) return;
    panning = false;
    if (
      Math.abs(pointer.clientX - startX) > tapDistance ||
      Math.abs(pointer.clientY - startY) > tapDistance){
      return;
    }
    const rect = modelViewer.current.getBoundingClientRect();
    const x = pointer.clientX - rect.left;
    const y = pointer.clientY - rect.top;
    const hit = modelViewer.current.positionAndNormalFromPoint(x, y);
    modelViewer.current.cameraTarget =
        hit == null ? 'auto auto auto' : hit.position.toString();
  };

  const progressCallback = (progressEvent:{detail:{totalProgress:number}}) => {
        setProgress(progressEvent.detail.totalProgress * 100);
        if(progressEvent.detail.totalProgress === 1 && autoplay){
          modelViewer.current.pause();
          setTimeout(() => {
            modelViewer.current.play();
          }, 250);
        }
        // console.log("Anims", modelViewer.current.availableAnimations);
        if(onLoad && progressEvent.detail.totalProgress === 1){
          setTimeout(() => {
            onLoad();
            // setOpacity(1);
          }, 500);
        }
  };


  React.useEffect(() => {
    if(modelViewer && modelViewer.current){
      modelViewer.current.onprogress = progressCallback;

      // modelViewer.current.addEventListener('play', (event: any) => {
      //   console.log("Start play", event);
      // }, true);

      // setOpacity(0);
      if(!noMove){
        modelViewer.current.addEventListener('mousedown', (event: any) => {
          startX = event.clientX;
          startY = event.clientY;
          panning = event.button === 2 || event.ctrlKey || event.metaKey ||
              event.shiftKey;
          if (!panning)
            return;
      
          lastX = startX;
          lastY = startY;
          startPan();
          event.stopPropagation();
        }, true);
      
        modelViewer.current.addEventListener('touchstart', (event: any) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          panning = event.touches.length === 2;
          if (!panning)
            return;
      
          const {touches} = event;
          lastX = 0.5 * (touches[0].clientX + touches[1].clientX);
          lastY = 0.5 * (touches[0].clientY + touches[1].clientY);
          startPan();
        }, true);
  
        modelViewer.current.addEventListener('mousemove', (event: any) => {
          if (!panning)
            return;
    
          movePan(event.clientX, event.clientY);
          event.stopPropagation();
        }, true);
      
        modelViewer.current.addEventListener('touchmove', (event: any) => {
          if (!panning || event.touches.length !== 2)
            return;
      
          const {touches} = event;
          const thisX = 0.5 * (touches[0].clientX + touches[1].clientX);
          const thisY = 0.5 * (touches[0].clientY + touches[1].clientY);
          movePan(thisX, thisY);
        }, true);
  
        window.addEventListener('mouseup', (event) => {
          recenter(event);
        }, true);
        
        window.addEventListener('touchend', (event) => {
          if (event.touches.length === 0) {
            recenter(event.changedTouches[0]);
          }
        }, true);
      }
    }
  }, [src])

  React.useImperativeHandle(ref, () => ({

    getAvailableAnimations() {
      // console.log("getAvailableAnimations", modelViewer.current);
      return modelViewer.current.availableAnimations;
    },
    playAnimation(name: string) {
      modelViewer.current.pause();
      if(!modelViewer.current.availableAnimations.includes(name)){
        throw new Error(`Animation ${name} not found in ${src}`);
      }
      modelViewer.current.animationName = name;
      modelViewer.current.play();
    },
    playAnimationOnce(name: string) {
      if(animationTimeout){
        clearTimeout(animationTimeout);
      }
      modelViewer.current.pause();
      // modelViewer.current.currentTime = 0;
      if(!modelViewer.current.availableAnimations.includes(name)){
        console.error(`Animation ${name} not found in ${src}`, modelViewer.current.availableAnimations);
        // throw new Error(`Animation ${name} not found in ${src}`);
      }
      modelViewer.current.animationName = name;
      const millisToEnd =  (modelViewer.current.duration - modelViewer.current.currentTime) * 1000;
      setAnimationTimeout(setTimeout(() => {
        // console.log("SET0", millisToEnd);
        modelViewer.current.pause();
        modelViewer.current.animationName = "";
        // modelViewer.current.currentTime = modelViewer.current.duration;
      }, millisToEnd));
      modelViewer.current.play();
    },
    playAnimationUntil(name: string, until:number) {
      if(animationTimeout){
        clearTimeout(animationTimeout);
      }
      modelViewer.current.pause();
      // modelViewer.current.currentTime = 0;
      if(!modelViewer.current.availableAnimations.includes(name)){
        console.error(`Animation ${name} not found in ${src}`, modelViewer.current.availableAnimations);
        // throw new Error(`Animation ${name} not found in ${src}`);
      }
      modelViewer.current.animationName = name;
      modelViewer.current.onplay = () =>{
        setAnimationTimeout(setTimeout(() => {
          modelViewer.current.pause();
          // modelViewer.current.currentTime = modelViewer.current.duration;
        }, until));
      }
      modelViewer.current.play();
    },
    stopAnimation() {
      // console.log("stopAnimation", modelViewer.current);
      modelViewer.current.pause();
    }

  }));

  const modelViewerConditionalAttributes:any = {}
  if(!noControls){
    modelViewerConditionalAttributes["camera-controls"] = true;
  }
  if(ar){
    modelViewerConditionalAttributes["ar"] = true;
    // modelViewerConditionalAttributes["ar-scale"] = "fixed";
    // modelViewerConditionalAttributes["ar-placement"] = "floor";
    modelViewerConditionalAttributes["ar-modes"] = "webxr scene-viewer quick-look";
  }
  if(autoplay){
    modelViewerConditionalAttributes["autoplay"] = true;
  }
  if(usdz){
    modelViewerConditionalAttributes["ios-src"] = usdz;
  }  
  if(hideShadow){
    modelViewerConditionalAttributes["shadow-intensity"] = "0";
  }else{
    modelViewerConditionalAttributes["shadow-intensity"] = "1";
  }

  return (
    <div className={styles.ModelViewerContainer}>
      <model-viewer 
        id={`model-viwer-${Math.random() * new Date().getTime()}`}
        ref={modelViewer}
        {...modelViewerConditionalAttributes}
        animation-crossfade-duration="0"
        max-field-of-view="90deg"
        loading="eager"
        style={{
          opacity: opacity,
          transition: 'opacity 750ms ease-in',
        }}
        animation-name={animationName || ""}
        src={src}
        environment-image="neutral">
          <div slot="ar-button" className={styles.ARButton} >
            <div className={'instruction-info-field'}>
              <div className={'instruction-info-back'}>
                <p style={{top:40, left:6}}>AR-Modus</p>
              </div>
              <IconButton 
                color="inherit"
              >
                <PhotoCameraRoundedIcon fontSize="large" />
              </IconButton >
            </div>
            {/* <IconButton 
              color="inherit"
              >
                <PhotoCameraRoundedIcon fontSize="large" />
            </IconButton > */}
          </div>
        </model-viewer>
        {progress < 100 ? (
          <div className={styles.ProgressContainer}>
              <CircularProgress color="inherit" />
              {/* <CircularProgress color="inherit" variant="determinate" value={progress} /> */}
              <span className={styles.Progress}>{Math.round(progress)}%</span>
          </div>
        ): null}
    </div>
  );
}
export default React.forwardRef(ModelViewer)